import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWindowSizeClass } from '@/hooks/useWindowSizeClass';
import { cn } from '@/lib/utils';
import { IconHome, IconSpaces, IconInventory, IconManifests, IconShipments } from '@/components/icons/Icons';
import { TopBar } from './TopBar';
import { BottomNav, type NavItem } from './BottomNav';
import { NavRail } from './NavRail';
import { NavDrawer } from './NavDrawer';
import styles from './AppShell.module.css';

/**
 * Main application shell that adapts navigation layout
 * based on MD3 window size class:
 * - compact  → TopBar + BottomNav
 * - medium / expanded → TopBar + NavRail
 * - large / xlarge → TopBar + NavDrawer
 */
export function AppShell() {
  const { t } = useTranslation('common');
  const sizeClass = useWindowSizeClass();

  const NAV_ITEMS: NavItem[] = [
    { to: '/', icon: <IconHome />, label: t('nav.home') },
    { to: '/spaces', icon: <IconSpaces />, label: t('nav.spaces') },
    { to: '/inventory', icon: <IconInventory />, label: t('nav.inventory') },
    { to: '/manifests', icon: <IconManifests />, label: t('nav.manifests') },
    { to: '/shipments', icon: <IconShipments />, label: t('nav.shipments') },
  ];

  const showBottomNav = sizeClass === 'compact';
  const showNavRail = sizeClass === 'medium' || sizeClass === 'expanded';
  const showNavDrawer = sizeClass === 'large' || sizeClass === 'xlarge';

  const contentClassName = cn(
    styles.content,
    showNavRail && styles.contentWithRail,
    showNavDrawer && styles.contentWithDrawer,
  );

  return (
    <div className={styles.shell}>
      <TopBar
        title={t('app_name')}
        onMenuClick={showBottomNav ? undefined : undefined}
      />

      <div className={styles.body}>
        {showNavRail && <NavRail items={NAV_ITEMS} />}
        {showNavDrawer && <NavDrawer items={NAV_ITEMS} />}

        <main id="main-content" className={contentClassName}>
          <Outlet />
        </main>
      </div>

      {showBottomNav && <BottomNav items={NAV_ITEMS} />}
    </div>
  );
}
